import React from "react";
import PolicyPage, { H2, LI, P, UL } from "../../components/PolicyPage";

const Mail = () => (
  <a href="mailto:info@nqsprings.com.au" className="gold-text font-semibold" data-testid="privacy-contact-email">info@nqsprings.com.au</a>
);

export default function Privacy() {
  return (
    <PolicyPage
      slug="privacy-policy"
      title="Privacy Policy"
      updated="November 2026"
      seoDescription="How NQ Springs collects, uses, discloses and protects your personal information under the Australian Privacy Principles."
      intro={'This Privacy Policy explains how NQ Springs (referred to as "we", "us", or "our") collects, uses, discloses, and protects your personal information in accordance with the Australian Privacy Principles (APPs) set out in the Privacy Act 1988 (Cth).'}
    >
      <section>
        <H2>1. The Information We Collect</H2>
        <P>
          We only collect personal information that is reasonably necessary to provide our automotive
          services and products, maintain business records, and communicate with you. This includes:
        </P>
        <UL>
          <LI><strong>Identity &amp; Contact Data:</strong> First name, last name, email address and phone number.</LI>
          <LI><strong>Address Data:</strong> Delivery addresses for online orders, and residential or business addresses where required for vehicle work such as GVM upgrades.</LI>
          <LI><strong>Vehicle Data:</strong> Car registration number, and vehicle make, model and build details.</LI>
          <LI><strong>Enquiry &amp; Order Data:</strong> The messages you send us (including through our website chat and SMS), quotes, and your order and servicing history.</LI>
          <LI><strong>Technical Data:</strong> IP address, browser type, and website interaction data (collected automatically).</LI>
        </UL>
      </section>

      <section>
        <H2>2. How We Collect Your Information</H2>
        <P>We collect your information in two ways:</P>
        <UL>
          <LI><strong>Directly from you:</strong> When you fill out a contact, quote, booking, build or enquiry form on our website, place an online order, chat with us through our website chat or SMS, or provide your details to us in-person, over the phone, or via email.</LI>
          <LI><strong>Automatically:</strong> Through cookies and tracking technologies when you browse our website.</LI>
        </UL>
        <P>Our website does not collect, process, or store credit card details or bank account information.</P>
      </section>

      <section>
        <H2>3. Third-Party Payment Processing (Stripe and Xero)</H2>
        <P>All payments are processed externally by secure payment providers:</P>
        <UL>
          <LI><strong>Online shop orders</strong> are paid through a secure checkout hosted by Stripe.</LI>
          <LI><strong>Workshop invoices</strong> are paid through a secure payment platform hosted by Xero.</LI>
          <LI>We do not view or store your financial credentials.</LI>
          <LI>Your payment data is handled entirely by Stripe or Xero in accordance with their own privacy and security protocols.</LI>
        </UL>
      </section>

      <section>
        <H2>4. Website Analytics, Advertising and Cookies</H2>
        <P>We use cookies, web beacons, Google Analytics and Google Ads to monitor and analyse traffic on our website and to measure our advertising.</P>
        <UL>
          <LI><strong>What are cookies?</strong> Cookies are small text files placed on your device to help our website remember your preferences and recognise your browser.</LI>
          <LI><strong>Google Analytics:</strong> This tool collects aggregated data about how visitors arrive at and interact with our website (e.g., pages viewed, time spent on site). It may collect your IP address and device information.</LI>
          <LI><strong>Google Ads:</strong> When you submit an enquiry, place an order, call us or message us through the website, we tell Google so we can measure which ads work. To match this to an ad, your email address or phone number may be shared with Google in a scrambled (hashed) form that does not reveal it.</LI>
          <LI><strong>Managing Cookies:</strong> You can choose to reject or block cookies by adjusting your internet browser settings. However, doing so may affect your user experience on our website.</LI>
        </UL>
      </section>

      <section>
        <H2>5. Data Storage and Service Providers</H2>
        <P>
          Your personal information, vehicle registration data, and communication history are stored
          securely within MechanicDesk, our specialised automotive Customer Relationship Management (CRM) platform.
        </P>
        <P>We also use trusted service providers to run our business, who hold information on our behalf:</P>
        <UL>
          <LI><strong>Our website system:</strong> online orders, website enquiries and our email mailing list.</LI>
          <LI><strong>Microsoft 365 and Google (Gmail):</strong> our business email.</LI>
          <LI><strong>Resend:</strong> to send our marketing and service emails.</LI>
          <LI><strong>Podium:</strong> our website chat and SMS messaging.</LI>
          <LI><strong>Stripe and Xero:</strong> payments and invoicing.</LI>
          <LI><strong>Google:</strong> website analytics and advertising measurement.</LI>
        </UL>
        <UL>
          <LI>Your data is hosted securely in accordance with industry-standard digital security and data protection protocols.</LI>
          <LI>We retain this information only for as long as necessary to manage your orders and vehicle servicing history and fulfil our legal and business requirements.</LI>
        </UL>
      </section>

      <section>
        <H2>6. Overseas Disclosure</H2>
        <P>
          Some of the service providers listed above store or process information outside Australia,
          mainly in the United States, and may also use data centres in other countries. We use
          reputable providers who protect personal information to industry security standards.
        </P>
      </section>

      <section>
        <H2>7. How We Use Your Information (Internal Marketing)</H2>
        <P>
          We use the information we hold to manage your customer profile, process your orders and look
          after your vehicle. Specifically, we use your name, email, phone and registration to:
        </P>
        <UL>
          <LI>Respond to your enquiries, prepare quotes and deliver your orders.</LI>
          <LI>Send service reminders, registration notices, and booking updates.</LI>
          <LI>Send internal marketing communications, special workshop offers, or seasonal promotions, where you are an existing customer or have opted in (for example by ticking the marketing box on one of our website forms).</LI>
        </UL>
        <P>
          We do not sell, rent, or trade your personal information to third-party marketing companies.
          All marketing is conducted strictly by us, internally.
        </P>
      </section>

      <section>
        <H2>8. Automated Decision-Making</H2>
        <P>
          We do not use computer programs to make decisions that could significantly affect your rights
          or interests. Decisions about your quotes, orders and vehicle work are made by our team.
        </P>
      </section>

      <section>
        <H2>9. Opting Out of Marketing</H2>
        <P>
          You have the right to opt out of receiving marketing or service reminder emails from us at any
          time. You can do this by:
        </P>
        <UL>
          <LI>Clicking the "unsubscribe" link at the bottom of any email we send.</LI>
          <LI>Contacting our workshop directly and requesting that your profile be updated to opt out.</LI>
        </UL>
      </section>

      <section>
        <H2>10. Accessing, Correcting and Deleting Your Information</H2>
        <P>
          You have the right to request access to the personal information we hold about you, or to ask
          that we correct any inaccuracies (such as an updated email or a new car registration). You can
          also ask us to delete information we no longer need to keep. Email <Mail /> and we will respond
          within 30 days.
        </P>
      </section>

      <section>
        <H2>11. Complaints</H2>
        <P>
          If you have a concern about how we have handled your personal information, please contact us
          first using the details below. We will acknowledge your complaint, look into it and respond in
          writing within 30 days.
        </P>
        <P>
          If you are not satisfied with our response, you can contact the Office of the Australian
          Information Commissioner (OAIC) at{" "}
          <a href="https://www.oaic.gov.au" target="_blank" rel="noreferrer" className="gold-text font-semibold" data-testid="privacy-oaic-link">oaic.gov.au</a>{" "}
          or on 1300 363 992.
        </P>
      </section>

      <section>
        <H2>12. Contact Us</H2>
        <P>
          If you have any questions about this Privacy Policy or wish to lodge a privacy complaint,
          please contact us at:
        </P>
        <UL>
          <LI><strong>Business Name:</strong> NQ Springs</LI>
          <LI><strong>Email:</strong> <Mail /></LI>
          <LI><strong>Phone:</strong>{" "}<a href="tel:0747748859" className="gold-text font-semibold" data-testid="privacy-contact-phone">07 4774 8859</a></LI>
          <LI><strong>Address:</strong> 321 Ingham Rd, Garbutt QLD 4814</LI>
        </UL>
      </section>
    </PolicyPage>
  );
}
